import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AgentLayout } from "@/components/AgentLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Megaphone, ChatsCircle, Handshake, Lifebuoy, Trophy, ChatCircleDots,
  PaperPlaneTilt, PushPin, PencilSimple, Trash, DotsThree, SmileyWink,
  CircleNotch, Lock, ShieldStar, MicrophoneSlash, Image as ImageIcon, X,
  Sparkle, Copy, Check, LinkSimple,
} from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const ICON_MAP = {
  Megaphone, ChatsCircle, Handshake, Lifebuoy, Trophy,
};

const REACTIONS = ["👍", "❤️", "🎉", "🔥", "👏", "😂"];
const POLL_MS = 4000;
const EDIT_WINDOW_MS = 5 * 60 * 1000;

// Auto-link URLs inside chat bodies. Only http/https schemes match — `javascript:`,
// `data:`, `vbscript:`, etc. are filtered out by the regex itself, never rendered as
// anchors. We also add rel="noopener noreferrer nofollow" + target=_blank.
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;
function safeLinkify(text) {
  if (!text) return null;
  const parts = String(text).split(URL_REGEX);
  return parts.map((part, i) => {
    // Odd indices are matched URLs (split with capturing group keeps them inline)
    if (i % 2 === 1 && /^https?:\/\//i.test(part)) {
      // Trim trailing punctuation that shouldn't be part of the URL.
      const m = part.match(/^(.*?)([.,;:!?)\]]+)$/);
      const url = m ? m[1] : part;
      const tail = m ? m[2] : "";
      return (
        <React.Fragment key={i}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-[#D4AF37] underline underline-offset-2 hover:text-[#F3C853] break-all"
            data-testid="chat-msg-link"
          >
            {url}
          </a>
          {tail}
        </React.Fragment>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function ChannelButton({ channel, active, onClick }) {
  const Icon = ICON_MAP[channel.icon] || ChatsCircle;
  return (
    <button
      onClick={onClick}
      data-testid={`chat-channel-${channel.id}`}
      className={`w-full text-left px-4 py-3 rounded-md flex items-start gap-3 transition-colors border ${
        active
          ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-white"
          : "bg-transparent border-transparent text-white/65 hover:bg-white/5 hover:text-white"
      }`}
    >
      <Icon size={18} weight="duotone" className={active ? "text-[#D4AF37] mt-0.5" : "text-white/50 mt-0.5"} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-sans text-[13px] truncate">#{channel.id}</span>
          {channel.admin_only_post && (
            <Lock size={10} weight="duotone" className="text-[#D4AF37]/70 shrink-0" />
          )}
        </div>
        <p className="text-[10px] text-white/40 truncate font-sans">{channel.description}</p>
      </div>
    </button>
  );
}

function ReactionPills({ msg, onReact }) {
  const entries = Object.entries(msg.reactions || {});
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {entries.map(([emoji, users]) => (
        <button
          key={emoji}
          onClick={() => onReact(msg.id, emoji)}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-white/5 hover:bg-[#D4AF37]/10 border border-white/10 hover:border-[#D4AF37]/40 transition-colors font-sans"
          data-testid={`chat-reaction-${msg.id}-${emoji}`}
        >
          <span>{emoji}</span>
          <span className="text-white/70">{users.length}</span>
        </button>
      ))}
    </div>
  );
}

function MessageItem({ msg, isAdmin, currentUserId, onReact, onPin, onDelete, onEdit, onMute }) {
  const [showActions, setShowActions] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState(msg.body);
  const isOwn = msg.author_id === currentUserId;
  const editable = isOwn && (Date.now() - new Date(msg.created_at).getTime() < EDIT_WINDOW_MS);
  const canDelete = isOwn || isAdmin;
  const initials = (msg.author_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const submitEdit = async () => {
    if (!draftBody.trim() || draftBody.trim() === msg.body) {
      setEditing(false);
      return;
    }
    await onEdit(msg.id, draftBody.trim());
    setEditing(false);
  };

  return (
    <div
      className={`group relative flex gap-3 px-4 py-3 rounded-md hover:bg-white/[0.03] transition-colors ${msg.pinned ? "border-l-2 border-[#D4AF37] bg-[#D4AF37]/5" : ""}`}
      data-testid={`chat-message-${msg.id}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { setShowActions(false); setShowPicker(false); }}
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] text-xs font-sans uppercase tracking-wider">
        {initials || "?"}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white text-sm font-sans" data-testid={`chat-msg-author-${msg.id}`}>{msg.author_name}</span>
          {msg.author_role === "admin" && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-1.5 py-0.5 rounded-full font-sans">
              <ShieldStar size={9} weight="duotone" /> Admin
            </span>
          )}
          <span className="text-white/40 text-[11px] font-sans">{fmtTime(msg.created_at)}</span>
          {msg.edited_at && (
            <span className="text-white/30 text-[10px] font-sans italic">(edited)</span>
          )}
          {msg.pinned && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#D4AF37] font-sans">
              <PushPin size={10} weight="fill" /> Pinned
            </span>
          )}
        </div>

        {editing ? (
          <div className="mt-1.5 space-y-2">
            <Textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              rows={2}
              className="bg-black/40 border-white/10 text-white text-sm focus:border-[#D4AF37]"
              data-testid={`chat-edit-input-${msg.id}`}
              autoFocus
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={submitEdit} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-8 text-[11px] uppercase tracking-widest" data-testid={`chat-edit-save-${msg.id}`}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraftBody(msg.body); }} className="text-white/70 h-8 text-[11px] uppercase tracking-widest">Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            {msg.body && (
              <p className="text-white/85 text-sm font-sans leading-relaxed mt-0.5 whitespace-pre-wrap break-words" data-testid={`chat-msg-body-${msg.id}`}>
                {safeLinkify(msg.body)}
              </p>
            )}
            {msg.image_url && (
              <a
                href={msg.image_url}
                target="_blank"
                rel="noreferrer"
                className="block mt-2 max-w-sm rounded-md overflow-hidden border border-white/10 hover:border-[#D4AF37]/40 transition-colors"
                data-testid={`chat-msg-image-${msg.id}`}
              >
                <img
                  src={msg.image_url}
                  alt="attachment"
                  loading="lazy"
                  className="w-full max-h-72 object-cover bg-black/40"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </a>
            )}
          </>
        )}

        <ReactionPills msg={msg} onReact={onReact} />
      </div>

      {/* Floating actions */}
      <div className={`absolute top-1 right-2 flex items-center gap-1 transition-opacity ${showActions ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="relative">
          <button
            onClick={() => setShowPicker((p) => !p)}
            className="p-1.5 rounded-md bg-ink-surface border border-white/10 hover:border-[#D4AF37]/50 text-white/70 hover:text-[#D4AF37]"
            title="React"
            data-testid={`chat-react-trigger-${msg.id}`}
          >
            <SmileyWink size={14} weight="duotone" />
          </button>
          {showPicker && (
            <div className="absolute top-9 right-0 z-10 flex gap-1 p-1.5 bg-ink-surface border border-white/15 rounded-md shadow-xl" data-testid={`chat-react-picker-${msg.id}`}>
              {REACTIONS.map((e) => (
                <button
                  key={e}
                  onClick={() => { onReact(msg.id, e); setShowPicker(false); }}
                  className="w-7 h-7 flex items-center justify-center rounded hover:bg-[#D4AF37]/15 text-base"
                  data-testid={`chat-react-${msg.id}-${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
        {editable && !editing && (
          <button onClick={() => setEditing(true)} className="p-1.5 rounded-md bg-ink-surface border border-white/10 hover:border-[#D4AF37]/50 text-white/70 hover:text-[#D4AF37]" title="Edit" data-testid={`chat-edit-${msg.id}`}>
            <PencilSimple size={14} weight="duotone" />
          </button>
        )}
        {isAdmin && (
          <button onClick={() => onPin(msg.id, !msg.pinned)} className={`p-1.5 rounded-md border ${msg.pinned ? "bg-[#D4AF37]/15 border-[#D4AF37]/50 text-[#D4AF37]" : "bg-ink-surface border-white/10 hover:border-[#D4AF37]/50 text-white/70 hover:text-[#D4AF37]"}`} title={msg.pinned ? "Unpin" : "Pin"} data-testid={`chat-pin-${msg.id}`}>
            <PushPin size={14} weight={msg.pinned ? "fill" : "duotone"} />
          </button>
        )}
        {isAdmin && !isOwn && (
          <button onClick={() => onMute(msg.author_id, msg.author_name)} className="p-1.5 rounded-md bg-ink-surface border border-white/10 hover:border-amber-400/50 text-white/70 hover:text-amber-300" title="Mute author" data-testid={`chat-mute-${msg.author_id}`}>
            <MicrophoneSlash size={14} weight="duotone" />
          </button>
        )}
        {canDelete && (
          <button onClick={() => onDelete(msg.id)} className="p-1.5 rounded-md bg-ink-surface border border-white/10 hover:border-red-400/50 text-white/70 hover:text-red-300" title="Delete" data-testid={`chat-delete-${msg.id}`}>
            <Trash size={14} weight="duotone" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [currentChannel, setCurrentChannel] = useState("general-chat");
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [muted, setMuted] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmMute, setConfirmMute] = useState(null);
  // Referral share URL (lazy-loaded for the Win template helper).
  const [referralShareUrl, setReferralShareUrl] = useState("");
  const [winTplAmount, setWinTplAmount] = useState("");
  const [winTplVariant, setWinTplVariant] = useState(0);
  const [winTplOpen, setWinTplOpen] = useState(false);
  const [winTplCopied, setWinTplCopied] = useState(false);
  // Insert-link helper
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);
  const isAdmin = user?.role === "admin";
  const channel = useMemo(() => channels.find((c) => c.id === currentChannel), [channels, currentChannel]);
  const adminOnlyPost = !!channel?.admin_only_post;
  const canPostHere = !muted && (!adminOnlyPost || isAdmin);
  // Image attach policy mirrors backend: 'none' | 'admin' | 'all'
  const imagePolicy = channel?.image_uploads || "none";
  const canAttachImage = canPostHere && (
    imagePolicy === "all" || (imagePolicy === "admin" && isAdmin)
  );

  // Auto-scroll to bottom whenever messages length changes
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, currentChannel]);

  // Initial channel + messages load (and on channel switch)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    setPinned([]);
    setImageUrl("");
    setShowImagePicker(false);
    lastTsRef.current = null;

    (async () => {
      try {
        if (!channels.length) {
          const { data } = await api.get("/chat/channels");
          if (!cancelled) setChannels(data.channels || []);
        }
        const { data } = await api.get(`/chat/messages?channel=${currentChannel}&limit=100`);
        if (cancelled) return;
        setMessages(data.messages || []);
        setPinned(data.pinned || []);
        setMuted(!!data.muted);
        const last = (data.messages || [])[data.messages.length - 1];
        lastTsRef.current = last ? last.created_at : data.now;
        setAccessError(null);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403) {
          setAccessError(err.response.data?.detail || "Chat is restricted");
        } else {
          toast.error("Could not load chat");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentChannel]); // eslint-disable-line

  // Polling for new messages + pin/reaction updates
  useEffect(() => {
    if (accessError) return;
    const interval = setInterval(async () => {
      try {
        const since = lastTsRef.current;
        // Always refresh full channel for reactions/edits/deletes/pins to stay in sync
        const { data } = await api.get(`/chat/messages?channel=${currentChannel}&limit=100`);
        setPinned(data.pinned || []);
        setMuted(!!data.muted);
        // Only update messages if anything changed (compare by length + last id+edited+reactions hash)
        const fresh = data.messages || [];
        setMessages((prev) => {
          if (prev.length !== fresh.length) return fresh;
          const a = JSON.stringify(prev.map((m) => [m.id, m.body, m.edited_at, m.reactions, m.pinned]));
          const b = JSON.stringify(fresh.map((m) => [m.id, m.body, m.edited_at, m.reactions, m.pinned]));
          return a === b ? prev : fresh;
        });
        const last = fresh[fresh.length - 1];
        if (last) lastTsRef.current = last.created_at;
        // unused-but-kept reference: 'since' guards initial pre-load (no-op when data unchanged)
        void since;
      } catch { /* swallow polling errors */ }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [currentChannel, accessError]);

  // Lazy-load this agent's referral share URL on the first visit to #wins.
  // Admins skip — they don't post wins from a personal referral pipeline.
  useEffect(() => {
    if (currentChannel !== "wins" || isAdmin || referralShareUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/agent/referrals");
        if (!cancelled && data?.share_url) setReferralShareUrl(data.share_url);
      } catch { /* silently ignore — template button stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [currentChannel, isAdmin, referralShareUrl]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    const url = imageUrl.trim();
    if (!body && !url) return;
    if (sending) return;
    if (url && !canAttachImage) {
      toast.error("Photos are not allowed in this channel");
      return;
    }
    if (url && !/^https?:\/\//i.test(url)) {
      toast.error("Image URL must start with http:// or https://");
      return;
    }
    setSending(true);
    try {
      const payload = { channel: currentChannel, body };
      if (url) payload.image_url = url;
      const { data } = await api.post("/chat/messages", payload);
      setMessages((m) => [...m, data]);
      setDraft("");
      setImageUrl("");
      setShowImagePicker(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  // Win template (#wins channel only, agents only) — turns each booking into a
  // marketing moment by inserting a pre-filled celebration + referral link.
  const showWinTemplate = currentChannel === "wins" && !isAdmin && !!referralShareUrl;
  const winTemplates = (amount, url) => {
    const a = (amount || "").trim();
    const dollar = a ? `a $${a}` : `an amazing`;
    return [
      `🎉 Just booked ${dollar} trip for a happy client! ✈️\n\nDM me for your free travel consultation — I'd love to help plan your next escape.\n\n👉 ${url}`,
      `✨ Another dream itinerary booked${a ? ` ($${a})` : ""}! Living for these client wins.\n\nThinking about your next trip? Let's chat — book your complimentary travel consultation here:\n${url}`,
      `🏝️ Booked ${dollar} getaway today! Nothing beats turning a wishlist into a confirmation number.\n\nWant me to plan yours? Free consult ⤵️\n${url}`,
    ];
  };
  const insertWinTemplate = () => {
    const tpl = winTemplates(winTplAmount, referralShareUrl)[winTplVariant];
    setDraft(tpl);
    setWinTplOpen(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="chat-composer-input"]');
      el?.focus?.();
    }, 50);
    toast.success("Template inserted — edit before posting!");
  };
  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralShareUrl);
      setWinTplCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setWinTplCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  // Insert-link helper — universal across channels. Sanitized to http(s) only;
  // the auto-linkifier will render the URL as a clickable anchor on send.
  const insertLink = () => {
    const url = linkUrl.trim();
    const text = linkText.trim();
    if (!url) {
      toast.error("Please enter a URL");
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    const snippet = text ? `${text} (${url})` : url;
    setDraft((d) => {
      if (!d) return snippet;
      return d.endsWith(" ") || d.endsWith("\n") ? d + snippet : d + " " + snippet;
    });
    setLinkText("");
    setLinkUrl("");
    setLinkOpen(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="chat-composer-input"]');
      el?.focus?.();
    }, 50);
    toast.success("Link inserted");
  };

  const onReact = async (msgId, emoji) => {
    try {
      const { data } = await api.post(`/chat/messages/${msgId}/react`, { emoji });
      setMessages((m) => m.map((x) => (x.id === msgId ? data : x)));
      setPinned((p) => p.map((x) => (x.id === msgId ? data : x)));
    } catch { toast.error("Reaction failed"); }
  };

  const onPin = async (msgId) => {
    try {
      await api.post(`/chat/messages/${msgId}/pin`);
      // Refresh both lists
      const { data } = await api.get(`/chat/messages?channel=${currentChannel}&limit=100`);
      setMessages(data.messages || []);
      setPinned(data.pinned || []);
      toast.success("Pin updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not pin"); }
  };

  const requestDelete = (msgId) => setConfirmDelete(msgId);
  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/chat/messages/${confirmDelete}`);
      setMessages((m) => m.filter((x) => x.id !== confirmDelete));
      setPinned((p) => p.filter((x) => x.id !== confirmDelete));
      toast.success("Message deleted");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
    setConfirmDelete(null);
  };

  const onEdit = async (msgId, body) => {
    try {
      const { data } = await api.put(`/chat/messages/${msgId}`, { body });
      setMessages((m) => m.map((x) => (x.id === msgId ? data : x)));
      setPinned((p) => p.map((x) => (x.id === msgId ? data : x)));
    } catch (err) { toast.error(err.response?.data?.detail || "Could not edit"); }
  };

  const requestMute = (uid, name) => setConfirmMute({ uid, name });
  const performMute = async () => {
    if (!confirmMute) return;
    try {
      await api.post("/chat/admin/mute", { user_id: confirmMute.uid, muted: true });
      toast.success(`${confirmMute.name} muted`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not mute"); }
    setConfirmMute(null);
  };

  if (accessError) {
    return (
      <AgentLayout>
        <div className="max-w-md mx-auto mt-20 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-400/30 mx-auto flex items-center justify-center mb-6">
            <Lock size={28} weight="duotone" className="text-amber-300" />
          </div>
          <h1 className="font-serif text-3xl text-white mb-3">Chat locked</h1>
          <p className="text-white/60 font-sans text-sm">{accessError}</p>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-2 font-sans">Community</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white">
          Agent <span className="italic text-[#D4AF37]">lounge</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[500px]" data-testid="chat-shell">
        {/* Channel sidebar */}
        <aside className="rounded-lg border border-white/10 bg-ink-surface p-2 overflow-y-auto" data-testid="chat-channel-list">
          <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-white/40 font-sans">Channels</p>
          <div className="space-y-1">
            {channels.map((c) => (
              <ChannelButton
                key={c.id}
                channel={c}
                active={c.id === currentChannel}
                onClick={() => setCurrentChannel(c.id)}
              />
            ))}
          </div>
        </aside>

        {/* Main column */}
        <section className="rounded-lg border border-white/10 bg-ink-surface flex flex-col overflow-hidden" data-testid="chat-main">
          {/* Header */}
          <header className="px-5 py-3 border-b border-white/10 flex items-center gap-3">
            <ChatCircleDots size={18} weight="duotone" className="text-[#D4AF37]" />
            <div className="min-w-0 flex-1">
              <h2 className="font-sans text-sm text-white truncate" data-testid="chat-current-channel">
                #{channel?.id || currentChannel}
              </h2>
              <p className="text-[10px] text-white/40 font-sans truncate">{channel?.description}</p>
            </div>
            {muted && (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/40 px-2 py-1 rounded-full" data-testid="chat-muted-pill">
                <MicrophoneSlash size={11} weight="duotone" /> Muted
              </span>
            )}
          </header>

          {/* Pinned */}
          {pinned.length > 0 && (
            <div className="px-4 py-2 border-b border-white/10 bg-[#D4AF37]/5" data-testid="chat-pinned-strip">
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]/80 font-sans flex items-center gap-1.5 mb-1.5">
                <PushPin size={10} weight="fill" /> Pinned · {pinned.length}
              </p>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {pinned.map((m) => (
                  <div key={`pin-${m.id}`} className="text-[12px] text-white/70 font-sans truncate">
                    <span className="text-[#D4AF37]">{m.author_name}:</span> {safeLinkify(m.body)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-1 py-2" data-testid="chat-messages-scroll">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <CircleNotch size={24} className="text-[#D4AF37] animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <ChatsCircle size={32} weight="duotone" className="text-white/30 mb-3" />
                <p className="font-serif text-xl text-white">No messages yet</p>
                <p className="text-white/50 text-sm font-sans mt-1">Be the first to say hello in #{channel?.id}.</p>
              </div>
            ) : (
              messages.map((m) => (
                <MessageItem
                  key={m.id}
                  msg={m}
                  isAdmin={isAdmin}
                  currentUserId={user?.id}
                  onReact={onReact}
                  onPin={onPin}
                  onDelete={requestDelete}
                  onEdit={onEdit}
                  onMute={requestMute}
                />
              ))
            )}
          </div>

          {/* Composer */}
          <form onSubmit={send} className="border-t border-white/10 bg-black/30" data-testid="chat-composer">
            {/* Image-URL row (collapsible) */}
            {canAttachImage && showImagePicker && (
              <div className="px-3 pt-3 pb-1 border-b border-white/5" data-testid="chat-image-picker">
                <div className="flex items-center gap-2">
                  <ImageIcon size={14} weight="duotone" className="text-[#D4AF37] shrink-0" />
                  <Input
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Paste an image URL (e.g. https://i.imgur.com/abc.jpg)"
                    className="bg-black/40 border-white/10 text-white text-xs h-8 focus:border-[#D4AF37]"
                    data-testid="chat-image-url-input"
                  />
                  {imageUrl && (
                    <button
                      type="button"
                      onClick={() => setImageUrl("")}
                      className="p-1.5 rounded-md text-white/50 hover:text-red-300 hover:bg-red-500/10 shrink-0"
                      title="Clear image"
                      data-testid="chat-image-clear"
                    >
                      <X size={13} weight="bold" />
                    </button>
                  )}
                </div>
                {imageUrl && /^https?:\/\//i.test(imageUrl) && (
                  <div className="mt-2 inline-block max-w-[180px] rounded-md overflow-hidden border border-[#D4AF37]/30">
                    <img
                      src={imageUrl}
                      alt="preview"
                      className="max-h-24 w-auto object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      data-testid="chat-image-preview"
                    />
                  </div>
                )}
                <p className="text-[10px] text-white/40 font-sans mt-1.5 italic">
                  Tip: paste a public image URL — nothing is uploaded or stored on our servers.
                </p>
              </div>
            )}

            <div className="p-3 flex items-end gap-2">
              {canAttachImage && (
                <button
                  type="button"
                  onClick={() => setShowImagePicker((s) => !s)}
                  disabled={!canPostHere || sending}
                  className={`h-11 w-11 shrink-0 rounded-md border flex items-center justify-center transition-colors ${
                    showImagePicker || imageUrl
                      ? "bg-[#D4AF37]/15 border-[#D4AF37]/50 text-[#D4AF37]"
                      : "bg-ink-surface border-white/10 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
                  } disabled:opacity-40`}
                  title="Attach image URL"
                  data-testid="chat-image-toggle"
                >
                  <ImageIcon size={16} weight="duotone" />
                </button>
              )}
              <Popover open={linkOpen} onOpenChange={setLinkOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={!canPostHere || sending}
                    className="h-11 w-11 shrink-0 rounded-md border bg-ink-surface border-white/10 text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 flex items-center justify-center transition-colors disabled:opacity-40"
                    title="Insert a link"
                    data-testid="chat-link-toggle"
                  >
                    <LinkSimple size={16} weight="duotone" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  className="w-[340px] bg-ink-surface border-white/15 p-4 text-white"
                  data-testid="chat-link-popover"
                >
                  <div className="flex items-start gap-2 mb-3">
                    <LinkSimple size={16} weight="duotone" className="text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="font-serif text-sm text-white">Insert link</p>
                      <p className="text-[11px] text-white/50 font-sans">
                        Renders as a clickable anchor when posted.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">
                        Display text (optional)
                      </Label>
                      <Input
                        value={linkText}
                        onChange={(e) => setLinkText(e.target.value)}
                        placeholder="e.g. Sandals supplier portal"
                        className="bg-black/40 border-white/10 text-white text-sm h-9"
                        data-testid="chat-link-text"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">
                        URL *
                      </Label>
                      <Input
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            insertLink();
                          }
                        }}
                        placeholder="https://example.com"
                        className="bg-black/40 border-white/10 text-white text-sm h-9"
                        data-testid="chat-link-url"
                      />
                    </div>
                    <Button
                      onClick={insertLink}
                      className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-9 w-full text-[11px] uppercase tracking-widest font-sans"
                      data-testid="chat-link-insert"
                    >
                      <LinkSimple size={13} weight="duotone" /> Insert into message
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              {showWinTemplate && (
                <Popover open={winTplOpen} onOpenChange={setWinTplOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      disabled={!canPostHere || sending}
                      className="h-11 shrink-0 rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] flex items-center gap-1.5 px-3 text-[10px] uppercase tracking-widest font-sans disabled:opacity-40"
                      title="Insert a celebration + referral link template"
                      data-testid="chat-win-template-btn"
                    >
                      <Sparkle size={14} weight="fill" />
                      <span className="hidden sm:inline">Win template</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="end"
                    className="w-[360px] bg-ink-surface border-white/15 p-4 text-white"
                    data-testid="chat-win-template-popover"
                  >
                    <div className="flex items-start gap-2 mb-3">
                      <Sparkle size={16} weight="fill" className="text-[#D4AF37] mt-0.5" />
                      <div>
                        <p className="font-serif text-sm text-white">Turn this win into a lead</p>
                        <p className="text-[11px] text-white/50 font-sans">
                          Pick a template and we'll inject your referral link. You can edit it before sending.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">
                          Trip value (optional)
                        </Label>
                        <Input
                          value={winTplAmount}
                          onChange={(e) => setWinTplAmount(e.target.value.replace(/[^0-9.,]/g, ""))}
                          placeholder="e.g. 12,500"
                          className="bg-black/40 border-white/10 text-white text-sm h-9"
                          data-testid="chat-win-template-amount"
                        />
                      </div>

                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">
                          Variant
                        </Label>
                        <div className="grid grid-cols-3 gap-1.5">
                          {[0, 1, 2].map((i) => (
                            <button
                              type="button"
                              key={i}
                              onClick={() => setWinTplVariant(i)}
                              className={`h-8 rounded-md border text-[11px] font-sans transition-colors ${
                                winTplVariant === i
                                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                                  : "bg-black/40 text-white/60 border-white/10 hover:border-[#D4AF37]/40"
                              }`}
                              data-testid={`chat-win-template-variant-${i}`}
                            >
                              {["Classic", "Casual", "Beach"][i]}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-md border border-white/10 bg-black/30 p-2.5 max-h-32 overflow-y-auto">
                        <p
                          className="text-[12px] text-white/75 font-sans whitespace-pre-wrap leading-relaxed"
                          data-testid="chat-win-template-preview"
                        >
                          {winTemplates(winTplAmount, referralShareUrl)[winTplVariant]}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          onClick={insertWinTemplate}
                          className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-9 flex-1 text-[11px] uppercase tracking-widest font-sans"
                          data-testid="chat-win-template-insert"
                        >
                          <Sparkle size={13} weight="fill" /> Insert
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={copyReferralLink}
                          className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-9 px-3 text-[10px] uppercase tracking-widest font-sans border border-white/10"
                          data-testid="chat-win-template-copy-link"
                          title={referralShareUrl}
                        >
                          {winTplCopied ? <Check size={13} weight="bold" /> : <Copy size={13} weight="duotone" />}
                          <span className="hidden sm:inline ml-1">Link</span>
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder={
                  muted
                    ? "You are muted in chat — contact an admin"
                    : adminOnlyPost && !isAdmin
                    ? `Only admins can post in #${currentChannel}`
                    : `Message #${currentChannel}…`
                }
                disabled={!canPostHere || sending}
                className="bg-ink border-white/10 text-white text-sm focus:border-[#D4AF37] resize-none min-h-[44px] max-h-32"
                data-testid="chat-composer-input"
              />
              <Button
                type="submit"
                disabled={!canPostHere || sending || (!draft.trim() && !imageUrl.trim())}
                className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 px-4"
                data-testid="chat-send-btn"
              >
                {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="duotone" />}
              </Button>
            </div>
          </form>
        </section>
      </div>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete message?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              This message will be removed for everyone. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5" data-testid="chat-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={performDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="chat-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mute confirm */}
      <AlertDialog open={!!confirmMute} onOpenChange={(o) => !o && setConfirmMute(null)}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Mute {confirmMute?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              They will still be able to read every channel but cannot post until you unmute them.
              You can unmute from the Admin Dashboard → Mutes section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5" data-testid="chat-mute-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={performMute} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="chat-mute-confirm">Mute</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AgentLayout>
  );
}
